import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  forwardRef,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { useAfterMountedOnBrowser } from '../../hooks/helpers/useAfterMountedOnBrowser';

import styles from './index.module.css';

export type RSTextFieldProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  labelText: string;
  placeholderText?: string;
  isBlock?: boolean;
  isError?: boolean;
};

export const RSTextField = forwardRef<HTMLInputElement, RSTextFieldProps>(
  ({ labelText, placeholderText = '  ', isBlock, isError, ...props }, ref) => {
    const { disabled, readOnly, required } = props;

    const intl = useIntl();

    const color = '#0066FF';

    const isDisabled = useMemo(() => {
      return disabled || readOnly;
    }, [disabled, readOnly]);

    const isTransitionReady = useAfterMountedOnBrowser();

    const transitionClasses = useMemo(() => {
      if (!isTransitionReady) {
        return undefined;
      }

      return ['transition'];
    }, [isTransitionReady]);

    const labelTextColorClasses = useMemo(() => {
      if (isDisabled) {
        return ['text-gray-400', 'peer-placeholder-shown:text-gray-200'];
      }

      if (isError) {
        return ['text-red-700'];
      }

      return [
        'text-gray-1000',
        'peer-focus:text-gray-1000',
        'peer-placeholder-shown:text-gray-400',
      ];
    }, [isError, isDisabled]);

    const bgColorClasses = useMemo(() => {
      if (isDisabled) {
        return ['bg-gray-10'];
      }

      if (isError) {
        return ['bg-red-10'];
      }

      return ['bg-white'];
    }, [isError, isDisabled]);

    return (
      <>
        <div
          className={classnames(
            'root',
            'relative',
            'box-border',
            !isBlock ? 'inline-block' : 'block w-full',
            'h-56',
            bgColorClasses,
            'rounded-15',
            isError && !isDisabled ? 'ring-red-700' : 'ring-gray-200',
            'ring-1',
            'transition-all',
            'focus-within:bg-white',
            'focus-within:ring-2',
          )}
        >
          <div className={classnames('box-border', 'w-full', 'h-full')}>
            <label
              className={classnames(
                'box-border',
                'block',
                'w-full',
                'h-full',
                !isDisabled ? 'cursor-text' : 'cursor-not-allowed',
                'rounded-15',
                'px-2',
              )}
            >
              <input
                {...props}
                ref={ref}
                placeholder={placeholderText}
                className={classnames(
                  'rs-font',
                  'input',
                  styles['input'],
                  'peer',
                  'relative',
                  'box-border',
                  'w-full',
                  'p-0',
                  'top-3',
                  'bottom-0.5',
                  'bg-transparent',
                  'text-16',
                  'pc:text-15',
                  !isDisabled ? 'text-gray-1000' : 'text-gray-400',
                  'font-normal',
                  'border-none',
                  'border-transparent',
                  'placeholder:text-gray-200',
                  'placeholder:font-normal',
                  'placeholder:opacity-0',
                  !isDisabled ? 'cursor-auto' : 'cursor-not-allowed',
                  'transition-opacity',
                  'focus:outline-none',
                  'focus:placeholder:opacity-100',
                )}
              />
              <span
                className={classnames(
                  'rs-font',
                  styles['label'],
                  'absolute',
                  'origin-top-left',
                  'select-none',
                  'text-16',
                  'pc:text-15',
                  labelTextColorClasses,
                  'font-normal',
                  transitionClasses,
                  'flex',
                  'items-center',
                  'max-w-[95%]', // 100%にするとtextareaの枠からはみ出るため
                  'overflow-hidden',
                  'whitespace-nowrap',
                )}
              >
                <span className="truncate">{labelText}</span>
                {required && (
                  <span className="text-red-700">
                    {intl.formatMessage({
                      defaultMessage: '（必須）',
                      id: '40kjG7',
                      description:
                        'src/reservation-page/forms/RSTextField/index.tsx',
                    })}
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>
        <style jsx>{`
          .root:focus-within {
            --tw-ring-color: ${color};
          }
          .input {
            caret-color: ${color};
          }
        `}</style>
      </>
    );
  },
);

RSTextField.displayName = 'RSTextField';
